/* eslint-disable no-debugger, no-restricted-imports */
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import round from 'lodash/round'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  AnonymousCreateReachPaymentDocument,
  AnonymousCreateReachPaymentMutation,
  AnonymousUpdateReachPaymentDocument,
  AnonymousUpdateReachPaymentMutation,
  CreateReachPaymentDocument,
  CreateReachPaymentMutation,
  UpdateReachPaymentDocument,
  UpdateReachPaymentMutation,
} from '__generated__/atlas-types'

import { handleChangeClientState } from 'utils/react-helmet'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { cleanGraphQLErrors, fromGlobalId } from 'utils/transformations/graphql'
import { toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { REACH_PROVIDER_NAME } from 'payments/payments.queries'
import { PENDING_STATUS } from 'payments/payments.transformations'
import { isAccountTypeBrand } from 'store/currentUser/selectors'
import { getDisplayLanguage } from 'store/currentUser/selectors'

import { Loader } from 'components/Core'

import {
  iframePaymentSession,
  isShowingBankTransferInstructions,
  pendingPaymentToAdd,
  temporaryPendingPayments,
} from '../../store'
import { refetchRemainingBalance } from '../helpers'
import { ReachIframeContainer, SelectPaymentMethodText } from '../styles'
import styles from './ReachIframe.module.css'
import {
  CREATE_PAYMENT_ERROR,
  NO_BILLING_COUNTRY_ERROR,
  PAYMENT_PROCESSING_ERROR,
  PAYMENT_SUCCEED,
  PAYMENT_SUCCEED_TS_IN_PROGRESS,
  PAYMENT_TRANSACTION_ERROR,
  UPDATE_PAYMENT_ERROR,
} from './constants'
import {
  BillingAddressDictionary,
  RateOffer,
  TransformedPaymentRecords,
} from 'features/Orders/types'

export const IFRAME_OPTIONS = {
  '--color-primary': '#323E48',
  '--font': 'Montserrat',
  '--accordion--border-radius': '0',
  '--accordion--border-top-width': '0',
  '--accordion--border-right-width': '0',
  '--accordion--border-left-width': '0',
  '--accordion--border-color': '#CDD4DA',
  '--button--border-radius': '5px',
}
export const IFRAME_TEXTS = {
  'body.payment.heading': '',
  'body.payment.subheading': '',
  'button.payment.complete': 'Complete Payment',
}
const CALLBACK_QUERY_PARAM = 'result'
const CALLBACK_SUCCESS = 'success'
const CALLBACK_ERROR = 'error'
const REACT_APP_REACH_IFRAME_LIBRARY =
  process.env.REACT_APP_REACH_IFRAME_LIBRARY || ''

const addPendingTransactionToReactiveVar = () => {
  const currentTransactions = temporaryPendingPayments()
  const pendingPayment = pendingPaymentToAdd()
  const addedPendingPayment = new Set(currentTransactions)
  addedPendingPayment.add(pendingPayment as TransformedPaymentRecords)
  temporaryPendingPayments([...addedPendingPayment])
}

type CallbackResultProps = {
  trackPaymentEvent: (eventName: string) => void
}

const CallbackResult = ({ trackPaymentEvent }: CallbackResultProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const resultSearchParam = queryParams.get(CALLBACK_QUERY_PARAM)

  useEffect(() => {
    if (resultSearchParam === CALLBACK_SUCCESS) {
      trackPaymentEvent('PAYMENTS - PAYMENT CALLBACK SUCCESS')
    }
    if (resultSearchParam === CALLBACK_ERROR) {
      trackPaymentEvent('PAYMENTS - PAYMENT CALLBACK ERROR')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultSearchParam])

  if (
    !resultSearchParam ||
    ![CALLBACK_SUCCESS, CALLBACK_ERROR].includes(resultSearchParam)
  ) {
    return null
  }

  const message =
    resultSearchParam === CALLBACK_SUCCESS
      ? PAYMENT_SUCCEED
      : PAYMENT_PROCESSING_ERROR

  const closeCallbackBox = () => {
    const queryParams = new URLSearchParams(location.search)
    if (queryParams.has(CALLBACK_QUERY_PARAM)) {
      queryParams.delete(CALLBACK_QUERY_PARAM)
      navigate(
        {
          search: queryParams.toString(),
        },
        { replace: true },
      )
    }
  }

  return (
    <div
      className={classNames({
        [styles.callbackError]: resultSearchParam === CALLBACK_ERROR,
        [styles.callbackSuccess]: resultSearchParam === CALLBACK_SUCCESS,
        [styles.callbackBox]: true,
      })}
    >
      <p>
        {message}
        <span
          className={styles.callbackBoxClose}
          onClick={closeCallbackBox}
          data-testid="closeCallbackBox"
        >
          X
        </span>
      </p>
    </div>
  )
}

interface PropsType {
  amountToPay: number
  billingAddressDictionary: BillingAddressDictionary
  brandId: string
  currencyCode: string
  orderId?: string
  retailerId?: string
  rateOffer?: RateOffer
  localCurrency?: string
  shouldUseLocalCurrencies: boolean
  setForceRerenderDate: Dispatch<SetStateAction<Date>>
  retailerName?: string
  externalPaymentId?: string
  shippingEmail?: string
  brandName?: string
  poNumber?: string
  orderTotalAmount?: number
}

const ReachIframe: FC<PropsType> = ({
  amountToPay,
  billingAddressDictionary,
  brandId,
  currencyCode,
  orderId,
  retailerId,
  rateOffer,
  localCurrency,
  shouldUseLocalCurrencies,
  setForceRerenderDate,
  retailerName,
  externalPaymentId,
  shippingEmail,
  brandName,
  poNumber,
  orderTotalAmount,
}) => {
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()
  const initialRenderRef = useRef(false)
  const [scriptLoaded, setScriptLoaded] = useState(!!window.RCH_CC)
  const [paymentSession, setPaymentSession] = useState('')
  const [createReachPaymentErrors, setCreateReachPaymentErrors] = useState<
    string[]
  >(!billingAddressDictionary.country ? [NO_BILLING_COUNTRY_ERROR] : [])

  const isBrandUser = useSelector(isAccountTypeBrand)
  const { externalPaymentUUID, uuid } = useParams<{
    externalPaymentUUID?: string
    uuid?: string
  }>()
  const userLanguage = useSelector(getDisplayLanguage)
  const { trackEvent } = useUserAnalytics()
  const trackPaymentEvent = (eventName: string) =>
    trackEvent(eventName, { orderId, brandId, retailerId })
  const useAnonymousMutations = !!externalPaymentUUID || !!uuid

  const localAmount = round(
    shouldUseLocalCurrencies && rateOffer?.Rate
      ? amountToPay / rateOffer?.Rate
      : amountToPay,
    2,
  )
  const [
    createReachPayment,
    { loading: loadingCreatePayment, called: calledCreatePayment },
  ] = useMutation(
    useAnonymousMutations
      ? AnonymousCreateReachPaymentDocument
      : CreateReachPaymentDocument,
    {
      client: atlasClient,
      onCompleted: (
        data: CreateReachPaymentMutation | AnonymousCreateReachPaymentMutation,
      ) => {
        if (!data) {
          return
        }
        const response =
          'createReachPayment' in data
            ? data.createReachPayment
            : data.anonymousCreateReachPayment
        if (response.errors && response.errors.length) {
          setCreateReachPaymentErrors(response.errors)
          return
        }
        setPaymentSession(response.sessionId)
      },
      onError: (resp) => {
        const errorMessages = cleanGraphQLErrors(resp.graphQLErrors)
        setCreateReachPaymentErrors(
          !isEmpty(errorMessages) ? errorMessages : [CREATE_PAYMENT_ERROR],
        )
      },
      errorPolicy: 'none',
    },
  )

  const [updateReachPayment, { loading: loadingUpdatePayment }] = useMutation(
    useAnonymousMutations
      ? AnonymousUpdateReachPaymentDocument
      : UpdateReachPaymentDocument,
    {
      client: atlasClient,
      onCompleted: (
        data: UpdateReachPaymentMutation | AnonymousUpdateReachPaymentMutation,
      ) => {
        if (!data) {
          return
        }

        const response =
          'updatePayment' in data
            ? data.updatePayment
            : data.anonymousUpdatePayment
        if (response.ok === false) {
          setCreateReachPaymentErrors([UPDATE_PAYMENT_ERROR])
        }
      },
      onError: () => {
        setCreateReachPaymentErrors([UPDATE_PAYMENT_ERROR])
        trackPaymentEvent('PAYMENTS - UPDATE PAYMENT FAILED')
      },
      errorPolicy: 'none',
    },
  )

  const paymentInProgress = loadingCreatePayment || loadingUpdatePayment

  /**
   * Keeps the data to be used for a "Waiting" payment up-to-date.
   */
  useEffect(() => {
    pendingPaymentToAdd({
      id: orderId ?? '',
      amount: shouldUseLocalCurrencies ? localAmount : amountToPay,
      currencyCode: currencyCode ?? '',
      externalIdentifier: paymentSession ?? '',
      status: PENDING_STATUS,
      date: new Date().toISOString(),
      last4: '',
      billingEmail: billingAddressDictionary.email,
      billingName: billingAddressDictionary.name,
      type: '',
      methodName: '',
      localAmount: shouldUseLocalCurrencies ? amountToPay : undefined,
      localCurrency,
    })
  }, [
    amountToPay,
    orderId,
    currencyCode,
    paymentSession,
    billingAddressDictionary,
    localAmount,
    localCurrency,
  ])

  useEffect(() => {
    if (!scriptLoaded || !paymentSession) {
      return
    }

    iframePaymentSession(paymentSession)
    const locale = window?.Localize?.getLanguage?.() || 'en'

    window.RCH_CC?.rchInit({
      target: '#rch-dropin',
      id: paymentSession,
      onSuccess: async () => {
        const savedSession = iframePaymentSession()
        if (paymentSession === savedSession || !savedSession) {
          trackPaymentEvent('PAYMENTS - PAYMENT SUCCESS TOASTER')
          renderSuccessToast({
            description: PAYMENT_SUCCEED_TS_IN_PROGRESS,
            shouldCloseManually: true,
          })
          addPendingTransactionToReactiveVar()
          setForceRerenderDate(new Date())
          refetchRemainingBalance({ isPublic: !!uuid })
        }
      },
      onFailure: () => {
        trackPaymentEvent('PAYMENTS - PAYMENT ERROR TOASTER')
        renderErrorToast({
          description: PAYMENT_TRANSACTION_ERROR,
          shouldCloseManually: true,
        })
      },
      customizationOptions: IFRAME_OPTIONS,
      text: IFRAME_TEXTS,
      // zh should be mapped to zh_CN since reach doesnt have a default language for zh
      locale: locale === 'zh' ? 'zh_CN' : locale,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded, paymentSession])

  // TODO:  move this logic into a custom hook
  useEffect(() => {
    if (initialRenderRef.current === false) {
      initialRenderRef.current = true
    } else {
      setForceRerenderDate(new Date())
    }
  }, [userLanguage, setForceRerenderDate])

  // TODO: refactor to use useEffect
  if (!calledCreatePayment && billingAddressDictionary.country) {
    createReachPayment({
      variables: {
        input: {
          retailerId,
          brandId,
          orderId,
          amount: amountToPay,
          currency: currencyCode,
          ...(shouldUseLocalCurrencies && {
            currency: localCurrency,
            invoiceCurrency: currencyCode,
            rateOfferId: rateOffer?.Id,
          }),
          paymentProvider: REACH_PROVIDER_NAME,
          taxAmount: 0,
          callbackUrl: window.location.href,
          externalPaymentId,
          billingProfile: {
            name: billingAddressDictionary.name || (retailerName ?? ''),
            email: billingAddressDictionary.email,
            phone: '',
            billingAddress: {
              street: billingAddressDictionary.street,
              city: billingAddressDictionary.city,
              country: billingAddressDictionary.country,
              region: billingAddressDictionary.state,
              zipCode: billingAddressDictionary.zip,
            },
          },
          viaAgent: isBrandUser,
          extraData: {
            brandName: brandName,
            retailerName: retailerName,
            retailerEmail: shippingEmail,
            poNumber: poNumber,
            orderTotalAmount: orderTotalAmount,
          },
        },
      },
      ...(uuid && {
        context: {
          headers: {
            'invoice-auth': uuid,
          },
        },
      }),
    })
  }

  // When amountToPay changes, update the session amount (updatePayment)
  useEffect(() => {
    if (!calledCreatePayment || paymentInProgress || !paymentSession) {
      return
    }
    // A bank transfer completes the session as soon as Reach issues the
    // instructions, so this session is already COMPLETED for Reach and the
    // update it rejects (INVALID_SESSION_STATE) used to replace the
    // instructions with an error (sc-260309). Nothing left to update here.
    if (isShowingBankTransferInstructions()) {
      return
    }
    const { id: brandIdOnly } = fromGlobalId(brandId)
    const { id: retailerIdOnly } = fromGlobalId(retailerId)
    updateReachPayment({
      variables: {
        input: {
          brandId: toGlobalId('Brand', brandIdOnly) ?? '',
          amount: amountToPay,
          paymentProvider: REACH_PROVIDER_NAME,
          externalIdentifier: paymentSession,
          ...(retailerIdOnly && {
            retailerId: toGlobalId('Retailer', retailerIdOnly),
          }),
        },
      },
      ...(uuid && {
        context: {
          headers: {
            'invoice-auth': uuid,
          },
        },
      }),
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountToPay])

  return (
    <ReachIframeContainer>
      <SelectPaymentMethodText>Select Payment Method</SelectPaymentMethodText>
      <Helmet
        onChangeClientState={handleChangeClientState(
          REACT_APP_REACH_IFRAME_LIBRARY,
          () => setScriptLoaded(true),
        )}
      >
        <script defer src={REACT_APP_REACH_IFRAME_LIBRARY} />
      </Helmet>
      <Loader active={paymentInProgress} dataTestId="iframe-loader">
        {!isEmpty(createReachPaymentErrors) ? (
          <ul className={styles.rch_error}>
            {createReachPaymentErrors.map((error) => (
              <li>{error}</li>
            ))}
          </ul>
        ) : (
          <>
            <CallbackResult trackPaymentEvent={trackPaymentEvent} />
            {amountToPay && <div id="rch-dropin" />}
          </>
        )}
      </Loader>
    </ReachIframeContainer>
  )
}

export default ReachIframe
